import { useEffect, useRef, useState } from 'react'
import { Modal } from './Modal'
import { Thumb } from './Thumb'
import { ImageLightbox } from './ImageLightbox'
import { api, ApiError, imageUrl } from '../lib/api'
import { prepareImage } from '../lib/image'
import { SUGGESTED_EMOJI } from '../lib/emoji'
import { useToast } from '../lib/toast'
import { ALLERGENS, INGREDIENTS, dietsRuledOutBy, listNames, type AllergenSlug, type Category, type Item, type ItemAllergen, type ProfileAllergen } from '@shared/types'


interface Props {
  categories: Category[]
  /** Existing item to edit, or null to create a new one. */
  item: Item | null
  defaultCategoryId: string
  /** Prefills the name of a new item, e.g. the food someone searched for and did not find. */
  defaultName?: string
  /** The child's allergens and school rules, so a match stands out while tagging. */
  watchFor: ProfileAllergen[]
  /**
   * The item as it stands after saving, or null when it has just been
   * deleted. Callers that only refresh a list may ignore it; the board uses
   * it to put a newly invented food straight into the slot it came from.
   */
  onSaved: (saved: Item | null) => void
  onClose: () => void
  onError: (err: unknown, fallback: string) => void
}

export function ItemEditor({ categories, item, defaultCategoryId, defaultName = '', watchFor, onSaved, onClose, onError }: Props) {
  const toast = useToast()
  const fileInput = useRef<HTMLInputElement>(null)

  const [name, setName] = useState(item?.name ?? defaultName)
  const [categoryId, setCategoryId] = useState(item?.categoryId ?? defaultCategoryId)
  const [emoji, setEmoji] = useState(item?.emoji ?? '')
  const [notes, setNotes] = useState(item?.notes ?? '')
  const [busy, setBusy] = useState(false)
  const [viewing, setViewing] = useState(false)
  const [dropping, setDropping] = useState(false)
  const [tags, setTags] = useState<ItemAllergen[]>(item?.allergens ?? [])
  const [otherTag, setOtherTag] = useState('')
  /* Ticking this is the parent saying they read the packet. It is the only
     thing in the app that counts as a check, so it is never ticked for them. */
  const [checked, setChecked] = useState(false)

  /*
   * A tag that matches something this family watches for is marked. Red is
   * reserved for anaphylaxis and school rules; a mild "avoid" is worth
   * seeing but not worth alarming anyone about.
   */
  const watched = new Set(
    watchFor.filter((a) => a.severity === 'severe' || a.kind === 'school').map((a) => a.slug),
  )
  /* Derived, never stored: the tags are the record, this is the reading. */
  const ruledOut = dietsRuledOutBy(tags)

  const hasTag = (slug: string, label = '') =>
    tags.some((t) => t.slug === slug && t.label.trim().toLowerCase() === label.trim().toLowerCase())
  const toggleTag = (slug: AllergenSlug) =>
    setTags((ts) => (hasTag(slug) ? ts.filter((t) => t.slug !== slug) : [...ts, { slug, label: '' }]))
  const addOtherTag = () => {
    const label = otherTag.trim().slice(0, 60)
    if (!label || hasTag('other', label)) return setOtherTag('')
    setTags((ts) => [...ts, { slug: 'other', label }])
    setOtherTag('')
  }

  /*
   * Photos are staged rather than uploaded on selection: a new item has no id
   * to attach an upload to yet, and staging means Cancel really does discard
   * everything rather than leaving an orphaned object in R2.
   */
  const [savedKey] = useState<string | null>(item?.imageKey ?? null)
  const [pending, setPending] = useState<{ blob: Blob; url: string } | null>(null)
  const [cleared, setCleared] = useState(false)

  // Revoke the preview URL when it is replaced or the editor closes.
  useEffect(() => () => {
    if (pending) URL.revokeObjectURL(pending.url)
  }, [pending])

  const previewKey = cleared ? null : savedKey
  const previewSrc = pending?.url ?? (previewKey ? imageUrl(previewKey) : null)

  async function stage(file: File | Blob) {
    try {
      const blob = await prepareImage(file instanceof File ? file : new File([file], 'pasted', { type: file.type }))
      setPending((prev) => {
        if (prev) URL.revokeObjectURL(prev.url)
        return { blob, url: URL.createObjectURL(blob) }
      })
      setCleared(false)
    } catch (err) {
      onError(err, 'Could not read that image.')
    }
  }

  /* ---- drag and drop ---------------------------------------------------- */

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    setDropping(false)
    const file = [...e.dataTransfer.files].find((f) => f.type.startsWith('image/'))
    if (file) void stage(file)
    else if (e.dataTransfer.files.length) toast.warn('That file is not an image.')
  }

  /* ---- paste ------------------------------------------------------------ */

  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      const image = [...(e.clipboardData?.items ?? [])].find((i) => i.type.startsWith('image/'))
      if (!image) return // let text paste through to the fields untouched
      const file = image.getAsFile()
      if (!file) return
      e.preventDefault()
      void stage(file)
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
    // `stage` is stable enough for this: it only closes over setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* ---- save ------------------------------------------------------------- */

  async function save() {
    const trimmed = name.trim()
    if (!trimmed || busy) return
    setBusy(true)
    try {
      const saved = item
        ? await api.updateItem(item.id, {
            name: trimmed,
            notes,
            emoji,
            categoryId,
            allergens: tags,
            allergensChecked: checked,
          })
        : await api.createItem({
            categoryId,
            name: trimmed,
            notes,
            emoji,
            allergens: tags,
            allergensChecked: checked,
          })

      if (pending) await api.uploadImage(saved.id, pending.blob)
      else if (cleared && savedKey) await api.removeImage(saved.id)

      onSaved(saved)
      onClose()
    } catch (err) {
      onError(err, err instanceof ApiError ? err.message : 'Could not save that item.')
      setBusy(false)
    }
  }

  async function remove() {
    if (!item) return
    setBusy(true)
    try {
      const { removedFromMeals } = await api.deleteItem(item.id)
      toast.notify(
        removedFromMeals > 0
          ? `Deleted ${item.name}, and took it out of ${removedFromMeals} saved meal${removedFromMeals === 1 ? '' : 's'}.`
          : `Deleted ${item.name}.`,
      )
      onSaved(null)
      onClose()
    } catch (err) {
      // The API refuses to delete anything a planned week still references.
      onError(err, 'Could not delete that item.')
      setBusy(false)
    }
  }

  async function toggleArchive() {
    if (!item) return
    setBusy(true)
    try {
      const saved = await api.updateItem(item.id, { archived: !item.archived })
      toast.notify(item.archived ? `${item.name} is back in the pantry.` : `Archived ${item.name}.`)
      onSaved(saved)
      onClose()
    } catch (err) {
      onError(err, 'Could not archive that item.')
      setBusy(false)
    }
  }

  return (
    <>
      <Modal
        title={item ? 'Edit item' : 'New item'}
        onClose={onClose}
        footer={
          <>
            {item && (
              <>
                <button className="btn sm" onClick={toggleArchive} disabled={busy}>
                  {item.archived ? 'Unarchive' : 'Archive'}
                </button>
                <button className="btn sm danger" onClick={remove} disabled={busy}>
                  Delete
                </button>
              </>
            )}
            <div className="spacer" />
            <button className="btn primary" onClick={save} disabled={busy || !name.trim()}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </>
        }
      >
        <div
          className={`dropzone ${dropping ? 'over' : ''}`}
          onDragOver={(e) => {
            e.preventDefault()
            setDropping(true)
          }}
          onDragLeave={() => setDropping(false)}
          onDrop={onDrop}
        >
          {previewSrc ? (
            <button
              type="button"
              className="dropzone-preview"
              onClick={() => setViewing(true)}
              aria-label="View photo full size"
            >
              <img src={previewSrc} alt="" />
            </button>
          ) : (
            <Thumb emoji={emoji} name={name || '?'} size="lg" />
          )}

          <div className="dropzone-body">
            <p className="dropzone-hint">
              Drop an image here, paste one, or{' '}
              <button type="button" className="linklike" onClick={() => fileInput.current?.click()}>
                choose a file
              </button>
              .
            </p>
            {(pending || previewKey) && (
              <button
                className="btn sm ghost"
                onClick={() => {
                  setPending((prev) => {
                    if (prev) URL.revokeObjectURL(prev.url)
                    return null
                  })
                  setCleared(true)
                }}
              >
                Remove photo
              </button>
            )}
            {pending && <span className="dropzone-staged">New photo ready — save to keep it.</span>}
          </div>

          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void stage(file)
              e.target.value = ''
            }}
          />
        </div>

        <div className="field">
          <label htmlFor="item-name">Name</label>
          <input
            id="item-name"
            type="text"
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            autoFocus
          />
        </div>

        <div className="field">
          <label htmlFor="item-cat">Category</label>
          <select id="item-cat" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.emoji} {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label>Emoji</label>
          <div className="emoji-grid">
            {SUGGESTED_EMOJI.map((e) => (
              <button
                key={e}
                type="button"
                className="emoji-btn"
                aria-pressed={emoji === e}
                onClick={() => setEmoji(emoji === e ? '' : e)}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label htmlFor="item-notes">Notes (optional)</label>
          <input
            id="item-notes"
            type="text"
            autoComplete="off"
            value={notes}
            placeholder="Brand, where to buy, how Ellis likes it…"
            maxLength={120}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        <div className="field">
          <label>Contains</label>
          <div className="allergen-chips">
            {ALLERGENS.map((a) => (
              <button
                key={a.slug}
                type="button"
                className={`btn sm ${hasTag(a.slug) ? (watched.has(a.slug) ? 'danger' : 'primary') : ''}`}
                aria-pressed={hasTag(a.slug)}
                onClick={() => toggleTag(a.slug)}
              >
                {hasTag(a.slug) ? '✓ ' : ''}
                {a.label}
              </button>
            ))}
          </div>
          {/*
            The rest of the ingredients a diet is checked against. They had no
            way in at all until recently: "Contains" offered the ten allergens
            and nothing else, so a hot dog could not be tagged as meat and
            quietly passed every vegetarian check.

            No heading calling these "the restriction ones". Four of the eight
            tags that make a food not vegan -- milk, egg, fish, shellfish --
            are up in the allergen row, so splitting the list that way would
            be telling the parent something untrue. They are all just things
            the food contains. What they add up to is said below, once.
          */}
          <div className="allergen-chips">
            {INGREDIENTS.map((a) => (
              <button
                key={a.slug}
                type="button"
                /*
                  Never the danger colour, even when a diet rules it out.
                  Red is anaphylaxis and a school ban -- a diet binds without
                  being dangerous, and the board, the shopping list and the
                  pantry badge all already say so in the right voice.
                */
                className={`btn sm ${hasTag(a.slug) ? 'primary' : ''}`}
                aria-pressed={hasTag(a.slug)}
                onClick={() => toggleTag(a.slug)}
              >
                {hasTag(a.slug) ? '✓ ' : ''}
                {a.label}
              </button>
            ))}
          </div>
          {tags.filter((t) => t.slug === 'other').length > 0 && (
            <div className="allergen-chips">
              {tags
                .filter((t) => t.slug === 'other')
                .map((t) => (
                  <span key={t.label} className="allergen-tag">
                    {t.label}
                    <button
                      type="button"
                      className="x"
                      aria-label={`Remove ${t.label}`}
                      onClick={() => setTags((ts) => ts.filter((x) => !(x.slug === 'other' && x.label === t.label)))}
                    >
                      ✕
                    </button>
                  </span>
                ))}
            </div>
          )}
          {/*
            What the ticks add up to, in the words the restrictions screen
            uses. Without it a parent has to know that vegan rules out honey
            and that vegetarian rules out gelatin, and that ticking Milk up
            in the allergen row is what makes a food not vegan -- which is
            exactly the arithmetic this app exists to stop people doing in
            their heads.
          */}
          {ruledOut.length > 0 && (
            <p className="allergen-verdict">
              <span className="allergen-verdict-mark" aria-hidden="true">
                ⊘
              </span>
              {/*
                "Rules it out for", not "Not vegetarian, not ..." -- one of
                the four restrictions is called "No pork", and prefixing that
                produced "not no pork". Naming them as the restrictions
                screen names them sidesteps the grammar and matches the words
                the parent picked over there.
              */}
              Rules it out for {listNames([...ruledOut])}
            </p>
          )}

          <div className="allergen-add">
            <div className="field">
              <input
                type="text"
                value={otherTag}
                placeholder="Something else it contains…"
                maxLength={60}
                aria-label="Another ingredient to record"
                onChange={(e) => setOtherTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addOtherTag()
                  }
                }}
              />
            </div>
            <button type="button" className="btn sm" disabled={!otherTag.trim()} onClick={addOtherTag}>
              Add
            </button>
          </div>

          <label className="allergen-checked">
            <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
            <span>I&rsquo;ve read this label</span>
          </label>
          <p className="muted small">
            {item?.allergensCheckedAt
              ? `Label last read by ${item.allergensCheckedBy || 'someone'} on ${new Date(
                  item.allergensCheckedAt.replace(' ', 'T') + 'Z',
                ).toLocaleDateString()}.`
              : 'Nobody has read this label yet. Untagged never means free of anything.'}{' '}
            Recipes change, so check the packet each time you buy it.
          </p>
        </div>
      </Modal>

      {viewing && previewSrc && (
        <ImageLightbox src={previewSrc} alt={name || 'Item photo'} onClose={() => setViewing(false)} />
      )}
    </>
  )
}
